import { useNavigate } from 'react-router-dom';
import Dropdown from '../Common/Dropdown';
import './navbar.css';
import { useState, useEffect, useRef, useMemo } from 'react';
// import { readSelectedLocation, writeSelectedLocation } from '../../utils/locationStore';

const NavBackButton = ({ onBack }: { onBack: () => void }) => {
	return (
		<div className="p-1 navbar-back-btn">
			<button
				className="pl-1 flex items-center hover:border-transparent outline-none focus:outline-none"
				onClick={onBack}
			>
				<svg
					width="10"
					height="10"
					viewBox="0 0 10 10"
					fill="none"
					xmlns="http://www.w3.org/2000/svg"
				>
					<path
						d="M9.16251 4.17008H2.83751L5.58751 1.42841C5.74443 1.27149 5.83259 1.05866 5.83259 0.836743C5.83259 0.614825 5.74443 0.401996 5.58751 0.245076C5.43059 0.0881567 5.21776 0 4.99584 0C4.77393 0 4.5611 0.0881567 4.40418 0.245076L0.237511 4.41174C0.161644 4.491 0.102173 4.58445 0.0625113 4.68674C-0.0208371 4.88963 -0.0208371 5.11719 0.0625113 5.32008C0.102173 5.42237 0.161644 5.51582 0.237511 5.59508L4.40418 9.76174C4.48165 9.83985 4.57381 9.90185 4.67536 9.94415C4.77691 9.98646 4.88583 10.0082 4.99584 10.0082C5.10585 10.0082 5.21478 9.98646 5.31633 9.94415C5.41787 9.90185 5.51004 9.83985 5.58751 9.76174C5.66562 9.68427 5.72761 9.59211 5.76992 9.49056C5.81223 9.38901 5.83401 9.28009 5.83401 9.17008C5.83401 9.06007 5.81223 8.95115 5.76992 8.8496C5.72761 8.74805 5.66562 8.65588 5.58751 8.57841L2.83751 5.83674H9.16251C9.38352 5.83674 9.59549 5.74895 9.75177 5.59267C9.90805 5.43639 9.99584 5.22442 9.99584 5.00341C9.99584 4.7824 9.90805 4.57044 9.75177 4.41415C9.59549 4.25787 9.38352 4.17008 9.16251 4.17008Z"
						fill="currentColor"
					/>
				</svg>

				<span className="pl-1 pe-2">Back</span>
			</button>
		</div>
	);
};

const NavTitle = ({ title }: { title: string }) => {
	return <div className="border-l-2 border-gray-200 p-1 pl-2 pr-2">{title}</div>;
};

const ALERT_LAYER_KEYS = ['anomaly', 'blockage', 'pumpAlert'] as const;
const VISUAL_LAYER_KEYS = ['rainfall'] as const;

type AlertLayerKey = (typeof ALERT_LAYER_KEYS)[number];
type VisualLayerKey = (typeof VISUAL_LAYER_KEYS)[number];

const convertLayerKey = (key: string) =>
	key
		.replace(/([A-Z])/g, ' $1')
		.replace(/_/g, ' ')
		.toLowerCase()
		.replace(/^\w/, (c) => c.toUpperCase());

type LayerState = {
	[assetTypeKey: string]: boolean;
} & {
	anomaly: boolean;
	blockage: boolean;
	pumpAlert: boolean;
	rainfall: boolean;
};

export const defaultLayerState: LayerState = {
	anomaly: true,
	blockage: true,
	pumpAlert: true,
	rainfall: true,
};

const RAIN_GAUGE_LABEL = 'RAIN GAUGE';

const findRainGaugeKey = (state: LayerState) =>
	Object.keys(state).find((key) => key.toLowerCase() === RAIN_GAUGE_LABEL.toLowerCase());

type LayersControlProps = {
	value?: LayerState;
	onChange?: (state: LayerState) => void;
};

const LayersControl = ({ value, onChange }: LayersControlProps) => {
	const [open, setOpen] = useState(false);
	const [state, setState] = useState(value ?? defaultLayerState);
	const containerRef = useRef<HTMLDivElement | null>(null);
	const rainGuageDefaultRef = useRef(false);

	useEffect(() => {
		if (!value) return;

		let next: LayerState = value;

		if (!rainGuageDefaultRef.current) {
			next = { ...value };

			const rainGaugeKey = findRainGaugeKey(next);

			if (rainGaugeKey) {
				next[rainGaugeKey] = false;
				rainGuageDefaultRef.current = true;
				onChange?.(next);
			}
		}

		setState(next);
	}, [value, onChange]);

	useEffect(() => {
		if (!open) return;

		const handleClickOutside = (event: MouseEvent) => {
			if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
				setOpen(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [open]);

	const toggle = (key: keyof LayerState) => {
		const next = { ...state, [key]: !state[key] };
		setState(next);
		onChange?.(next);
	};

	const buttonClass =
		`layers-btn shadow-2xl rounded-lg p-1 flex items-center justify-center` +
		`outline-none focus:outline-none cursor-pointer ` +
		(open ? 'layers-btn--active' : '');

	const allKeys = Object.keys(state);

	const assetKeys = allKeys
		.filter(
			(k) =>
				!ALERT_LAYER_KEYS.includes(k as AlertLayerKey) &&
				!VISUAL_LAYER_KEYS.includes(k as VisualLayerKey),
		)
		.sort();

	const alertKeys = ALERT_LAYER_KEYS.filter((k) => k in state);
	const visualKeys = VISUAL_LAYER_KEYS.filter((k) => k in state);

	const makeItemId = (section: 'asset' | 'alert' | 'visual', key: string) =>
		`layers-${section}-${key}`;

	return (
		<div ref={containerRef} className="relative ml-2 z-50">
			<div
				className={buttonClass}
				id={open ? 'layers-btn--active' : 'layers-btn'}
				onClick={() => setOpen((v) => !v)}
			>
				<svg
					width="24"
					height="24"
					viewBox="0 0 24 24"
					fill="none"
					xmlns="http://www.w3.org/2000/svg"
				>
					<path
						d="M20.5488 15.8984C21.0316 15.6304 21.64 15.8044 21.9082 16.2871C22.1761 16.7698 22.0022 17.3784 21.5195 17.6465L12.0342 22.916L2.54883 17.6465C2.06616 17.3783 1.89222 16.7698 2.16016 16.2871C2.42834 15.8044 3.03677 15.6304 3.51953 15.8984L12.0342 20.6279L20.5488 15.8984ZM20.5488 11.8984C21.0316 11.6304 21.64 11.8044 21.9082 12.2871C22.1761 12.7698 22.0022 13.3784 21.5195 13.6465L12.0342 18.916L2.54883 13.6465C2.06616 13.3783 1.89222 12.7698 2.16016 12.2871C2.42834 11.8044 3.03677 11.6304 3.51953 11.8984L12.0342 16.6279L20.5488 11.8984ZM11.5254 2.07324C11.8392 1.88781 12.2292 1.88781 12.543 2.07324L21.5771 7.41113C22.2321 7.79824 22.2321 8.74675 21.5771 9.13379L12.543 14.4717L12.4219 14.5322C12.1328 14.6539 11.7999 14.6339 11.5254 14.4717L2.49121 9.13379C1.83623 8.74676 1.83633 7.79825 2.49121 7.41113L11.5254 2.07324ZM4.96582 8.27246L12.0342 12.4492L19.1016 8.27246L12.0342 4.09473L4.96582 8.27246Z"
						fill="currentColor"
					/>
				</svg>
			</div>

			{open && (
				<div className="layers-menu absolute left-12 top-0 rounded-lg bg-white shadow-2xl py-3 px-3 text-sm">
					{assetKeys.length > 0 && (
						<div className="layers-section">
							<div className="layers-section-title">Assets</div>
							{assetKeys.map((key) => {
								const id = makeItemId('asset', key);
								const isChecked = !!state[key];
								return (
									<label
										key={key}
										id={id}
										className={`layers-item ${isChecked ? 'is-checked' : ''}`}
									>
										<input
											id={`${id}-input`}
											type="checkbox"
											checked={isChecked}
											onChange={() => toggle(key as keyof LayerState)}
										/>
										<span>{convertLayerKey(key)}</span>
									</label>
								);
							})}
						</div>
					)}

					{alertKeys.length > 0 && (
						<div className="layers-section">
							<div className="layers-section-title">Alerts</div>
							{alertKeys.map((key) => {
								const id = makeItemId('alert', key);
								const isChecked = !!state[key];

								return (
									<label
										key={key}
										id={id}
										className={`layers-item ${isChecked ? 'is-checked' : ''}`}
									>
										<input
											id={`${id}-input`}
											type="checkbox"
											checked={isChecked}
											onChange={() => toggle(key as keyof LayerState)}
										/>
										<span>{convertLayerKey(key)}</span>
									</label>
								);
							})}
						</div>
					)}

					{visualKeys.length > 0 && (
						<div className="layers-section">
							<div className="layers-section-title">Visual</div>
							{visualKeys.map((key) => {
								const id = makeItemId('visual', key);
								const isChecked = !!state[key];

								return (
									<label
										key={key}
										id={id}
										className={`layers-item ${isChecked ? 'is-checked' : ''}`}
									>
										<input
											id={`${id}-input`}
											type="checkbox"
											checked={isChecked}
											onChange={() => toggle(key as keyof LayerState)}
										/>
										<span>{convertLayerKey(key)}</span>
									</label>
								);
							})}
						</div>
					)}
				</div>
			)}
		</div>
	);
};

const readSelectedLocationId = (): string | number | null => {
	const raw = localStorage.getItem('location');
	if (!raw) return null;

	try {
		const parsed = JSON.parse(raw);
		if (parsed && typeof parsed === 'object') {
			return parsed.locationId ?? parsed.id ?? null;
		}
		if (typeof parsed === 'string' || typeof parsed === 'number') return parsed;
	} catch {
		// if someone stored a plain string
		return raw;
	}

	return null;
};

const writeSelectedLocationId = (
	next: string | number | null,
	locationOptions: Array<{ value: string; displayText: string }>,
) => {
	if (next == null) {
		localStorage.removeItem('location');
	} else {
		const opt = locationOptions.find((o) => String(o.value) === String(next));
		localStorage.setItem(
			'location',
			JSON.stringify({
				locationId: next,
				// optional, handy for debugging/other screens
				label: opt?.displayText,
			}),
		);
	}

	window.dispatchEvent(new Event('nwl:locations-updated'));
};

type NavbarProps = {
	title?: string;
	dropdownEmptyText?: string;
	locationOptions?: Array<{ value: string; displayText: string }>;
	onBack?: () => void;
	activeLocation?: string | number | null;
	onLocationChange?: (value: string | number | null) => void;
	layerState?: LayerState;
	onLayerStateChange?: (state: LayerState) => void;
};

const Navbar = ({
	title = 'Strategy Recommendation',
	dropdownEmptyText = 'Choose location',
	locationOptions = [],
	onBack = () => {},
	activeLocation = null,
	onLocationChange = () => {},
	layerState,
	onLayerStateChange,
}: NavbarProps) => {
	const navigate = useNavigate();
	const [syncedLocation, setSyncedLocation] = useState(() => {
		return readSelectedLocationId() ?? activeLocation ?? null;
	});

	const sortedLocationOptions = useMemo(() => {
		return [...locationOptions].sort((a, b) =>
			String(a.displayText ?? '').localeCompare(String(b.displayText ?? ''), undefined, {
				sensitivity: 'base',
			}),
		);
	}, [locationOptions]);

	useEffect(() => {
		const sync = () => {
			const next = readSelectedLocationId();
			setSyncedLocation(next);

			if (String(next ?? '') !== String(activeLocation ?? '')) {
				onLocationChange(next);
			}
		};

		sync();
		window.addEventListener('nwl:locations-updated', sync);
		window.addEventListener('storage', sync);

		return () => {
			window.removeEventListener('nwl:locations-updated', sync);
			window.removeEventListener('storage', sync);
		};
	}, [activeLocation, onLocationChange]);
	const effectiveActiveLocation = syncedLocation ?? activeLocation ?? null;

	const selectedIndex = useMemo(() => {
		const idx = sortedLocationOptions.findIndex(
			(o) => String(o.value) === String(effectiveActiveLocation),
		);
		return idx >= 0 ? idx : 0;
	}, [sortedLocationOptions, effectiveActiveLocation]);

	const commitLocationChange = (next: string | number | null) => {
		if (String(next ?? '') === String(effectiveActiveLocation ?? '')) return;

		writeSelectedLocationId(next, sortedLocationOptions);
		onLocationChange(next);

		setTimeout(() => navigate(0), 0);
	};

	const handleLocationChange = (a: any, b?: any) => {
		let next: string | number | null = null;

		if (b && typeof b === 'object' && 'value' in b) next = (b as any).value;
		else if (a && typeof a === 'object' && 'value' in a) next = (a as any).value;
		else if (typeof a === 'number') {
			const isIndex = a >= 0 && a < sortedLocationOptions.length;
			next = isIndex
				? (sortedLocationOptions[a]?.value ?? null)
				: (sortedLocationOptions.find((o) => String(o.value) === String(a))?.value ?? null);
		} else {
			next = sortedLocationOptions.find((o) => String(o.value) === String(a))?.value ?? a ?? null;
		}

		commitLocationChange(next);
	};

	return (
		<div className="navbar">
			<div className="flex items-center bg-white shadow-2xl rounded-lg">
				<NavBackButton onBack={onBack} />
				<NavTitle title={title} />
			</div>
			<div className="ml-2 flex items-center">
				<div className="flex items-center bg-white shadow-2xl rounded-lg pl-[3px] pr-[3px] pt-[3px] pb-[3px] w-[256px] relative">
					<div className="relative flex items-center w-full">
						<Dropdown
							icon={
								<svg
									width="15"
									height="18"
									viewBox="0 0 16 20"
									fill="none"
									xmlns="http://www.w3.org/2000/svg"
								>
									<path
										d="M8 0C5.87827 0 3.84344 0.842855 2.34315 2.34315C0.842855 3.84344 0 5.87827 0 8C0 13.4 7.05 19.5 7.35 19.76C7.53113 19.9149 7.76165 20.0001 8 20.0001C8.23835 20.0001 8.46887 19.9149 8.65 19.76C9 19.5 16 13.4 16 8C16 5.87827 15.1571 3.84344 13.6569 2.34315C12.1566 0.842855 10.1217 0 8 0ZM8 17.65C5.87 15.65 2 11.34 2 8C2 6.4087 2.63214 4.88258 3.75736 3.75736C4.88258 2.63214 6.4087 2 8 2C9.5913 2 11.1174 2.63214 12.2426 3.75736C13.3679 4.88258 14 6.4087 14 8C14 11.34 10.13 15.66 8 17.65ZM8 4C7.20887 4 6.43552 4.2346 5.77772 4.67412C5.11992 5.11365 4.60723 5.73836 4.30448 6.46927C4.00173 7.20017 3.92252 8.00444 4.07686 8.78036C4.2312 9.55628 4.61216 10.269 5.17157 10.8284C5.73098 11.3878 6.44371 11.7688 7.21964 11.9231C7.99556 12.0775 8.79983 11.9983 9.53073 11.6955C10.2616 11.3928 10.8864 10.8801 11.3259 10.2223C11.7654 9.56448 12 8.79113 12 8C12 6.93913 11.5786 5.92172 10.8284 5.17157C10.0783 4.42143 9.06087 4 8 4ZM8 10C7.60444 10 7.21776 9.8827 6.88886 9.66294C6.55996 9.44318 6.30362 9.13082 6.15224 8.76537C6.00087 8.39991 5.96126 7.99778 6.03843 7.60982C6.1156 7.22186 6.30608 6.86549 6.58579 6.58579C6.86549 6.30608 7.22186 6.1156 7.60982 6.03843C7.99778 5.96126 8.39991 6.00087 8.76537 6.15224C9.13082 6.30362 9.44318 6.55996 9.66294 6.88886C9.8827 7.21776 10 7.60444 10 8C10 8.53043 9.78929 9.03914 9.41421 9.41421C9.03914 9.78929 8.53043 10 8 10Z"
										fill="currentColor"
									/>
								</svg>
							}
							options={sortedLocationOptions}
							selectedIndex={selectedIndex}
							onChange={(a: any, b?: any) => handleLocationChange(a, b)}
							emptySelectionText={dropdownEmptyText}
							showActiveBadge={false}
							showMainBadge={false}
						/>
					</div>
				</div>
				<LayersControl value={layerState} onChange={onLayerStateChange} />
			</div>
		</div>
	);
};

export default Navbar;
